import { Card, Table } from 'antd';
import { CheckCircle, InfoCircle, XCircle } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

interface UploadResultSummaryProps {
  response: any;
}

export const UploadResultSummary: React.FC<UploadResultSummaryProps> = ({ response }) => {
  const { t } = useTranslation(['dataUpload', 'productAndBasketManagement']);

  if (!response) return null;

  const statusCode = response?.statusCode || response?.status;
  const isError = statusCode === 400 || response?.statusText === "ERROR";

  if (isError) {
    if (
      !response?.details ||
      !Array.isArray(response?.details) ||
      response?.details?.length === 0
    ) {
      return null;
    }

    const mainMessage = response?.message || t('uploadFailed');

    const isStringArray = typeof response?.details[0] === 'string';

    const errorColumns = isStringArray
      ? [
          {
            title: t('issue'),
            dataIndex: 'issue',
            key: 'issue',
            className: 'whitespace-normal text-red-600 font-medium',
          },
        ]
      : [
          {
            title: t('source'),
            dataIndex: 'source',
            key: 'source',
            className: 'whitespace-normal font-medium text-gray-700',
            width: '35%',
          },
          {
            title: t('issue'),
            dataIndex: 'issue',
            key: 'issue',
            className: 'whitespace-normal text-red-600 font-medium',
          },
        ];

    const errorTableData = response?.details?.map((detail: any, index: number) => {
      if (isStringArray) {
        return {
          key: index,
          issue: String(detail),
        };
      }

      const name = detail?.rowName || detail?.columnName || t('unknown');
      const expected = detail?.expectedRow ?? detail?.expectedCol;
      const found = detail?.foundAtRow ?? detail?.foundAtCol;

      return {
        key: index,
        source: name,
        issue: `${t('expectedAt')}: ${expected} | ${t('foundAt')}: ${found}`,
      };
    });

    return (
      <Card className="mb-5 w-full rounded-xl border-red-200 bg-red-50/40 shadow-sm">
        <div className="mb-4 flex items-start gap-3">
          <XCircle className="mt-0.5 shrink-0 text-red-500" size={22} />
          <div className="w-full">
            <h4 className="m-0 md:text-base font-semibold text-red-800 text-sm">
              {typeof mainMessage === 'string' ? mainMessage : t('uploadFailedHeader')}
            </h4>
          </div>
        </div>

        <div className="w-full overflow-hidden rounded-lg border border-red-100 bg-white">
          <Table
            columns={errorColumns}
            dataSource={errorTableData}
            pagination={errorTableData?.length > 5 ? { size: 'small', pageSize: 5 } : false}
            size="small"
            bordered={false}
            className="w-full"
          />
        </div>
      </Card>
    );
  }

  const data = response?.data?.data || response;

  if (data?.stats !== undefined) {
    const statsColumns = [
      {
        title: t('productAndBasketManagement:item'),
        dataIndex: 'item',
        key: 'item',
        className: 'whitespace-normal font-medium text-gray-700 text-sm md:text-base',
      },
      {
        title: t('productAndBasketManagement:inserted'),
        dataIndex: 'inserted',
        key: 'inserted',
        align: 'left' as const,
        className: 'font-semibold text-green-600 text-sm md:text-base',
        render: (value: any) => <span>{value}</span>,
      },
      {
        title: t('productAndBasketManagement:skipped'),
        dataIndex: 'skipped',
        key: 'skipped',
        align: 'left' as const,
        className: 'font-semibold text-gray-500 text-sm md:text-base',
        render: (value: any) => <span>{value}</span>,
      },
    ];

    const statsData = [
      { key: 'categories', item: t('productAndBasketManagement:categories'), ...data.stats.categories },
      { key: 'mainProducts', item: t('productAndBasketManagement:mainProducts'), ...data.stats.mainProducts },
      { key: 'presentations', item: t('productAndBasketManagement:presentations'), ...data.stats.presentations },
      { key: 'brands', item: t('productAndBasketManagement:brands'), ...data.stats.brands },
      { key: 'products', item: t('productAndBasketManagement:products'), ...data.stats.products },
      { key: 'baskets', item: t('productAndBasketManagement:baskets'), ...data.stats.baskets },
      { key: 'basketLinks', item: t('productAndBasketManagement:basketLinks'), ...data.stats.basketLinks },
      { key: 'establishments', item: t('productAndBasketManagement:establishments'), ...data.stats.establishments },
    ];

    const importErrors = data?.errors || [];
    const hasErrors = importErrors.length > 0;

    return (
      <Card
        className={`mb-5 rounded-xl shadow-sm ${
          hasErrors
            ? 'border-amber-200 bg-amber-50/30'
            : 'border-green-200 bg-green-50/30'
        }`}
      >
        <div className="mb-4 flex items-start gap-3">
          {hasErrors ? (
            <InfoCircle className="mt-0.5 shrink-0 text-amber-600" size={24} />
          ) : (
            <CheckCircle className="mt-0.5 shrink-0 text-green-600" size={24} />
          )}
          <div>
            <h4 className={`text-base font-semibold sm:text-lg ${hasErrors ? 'text-amber-800' : 'text-green-800'}`}>
              {hasErrors
                ? t('productAndBasketManagement:importResult')
                : t('productAndBasketManagement:importSuccess')}
            </h4>
          </div>
        </div>

        <div className="w-full overflow-hidden rounded-lg border border-gray-100 bg-white">
          <Table
            columns={statsColumns}
            dataSource={statsData}
            pagination={false}
            size="small"
            bordered={false}
            className="w-full"
          />
        </div>

        {hasErrors && (
          <div className="mt-4 rounded-lg border border-red-100 bg-red-50/50 p-3">
            <h5 className="font-semibold text-red-800 text-xs mb-2">
              {t('productAndBasketManagement:rowErrors')}
            </h5>
            <div className="max-h-48 space-y-1 overflow-y-auto pr-1 text-xs text-red-700">
              {importErrors.map((err: string, idx: number) => (
                <p
                  key={idx}
                  className="break-words border-b border-red-100/50 pb-1 leading-relaxed last:border-0"
                >
                  • {err}
                </p>
              ))}
            </div>
          </div>
        )}
      </Card>
    );
  }

  if (statusCode === 201 || data?.total !== undefined) {
    const getFieldLabel = (key: string) => {
      const labels: Record<string, string> = {
        total: t('total'),
        processed: t('processed'),
        skipped: t('skipped'),
        zeroValuesNormalized: t('normalized'),
      };
      return labels[key] || key;
    };

    const columns = [
      {
        title: t('description'),
        dataIndex: 'field',
        key: 'field',
        className: 'whitespace-normal font-medium text-gray-600 text-sm md:text-base',
        render: (text: string, record: any) => {
          if (record?.key === 'zeroValuesNormalized') {
            return (
             <div className="block w-full break-words relative pr-0 md:pr-2">
              <span className="inline align-middle leading-normal">
                {text}
                <span className="inline-block align-middle ml-2 md:ml-4 p-0.5 mb-0.5 flex-shrink-0">
                  <InfoCircle 
                    size={13} 
                    className="text-blue-500 vertical-middle" 
                    title={t('zeroValuesNormalizedInfo')}
                  />
                </span>
              </span>
            </div>
            );
          }
          return text;
        },
      },
      {
        title: t('count'),
        dataIndex: 'value',
        key: 'value',
        align: 'left' as const,
        className: 'font-semibold text-sm md:text-base',
        render: (value: any, record: any) => (
          <span className={record.color || 'text-gray-900'}>{value}</span>
        ),
      },
    ];

    const tableData: any[] = [];
    ['total', 'processed','skipped','zeroValuesNormalized'].forEach((key) => {
      if (data[key] !== undefined) {
        let color = 'text-gray-900';
        if (key === 'processed') color = 'text-green-600';
        if (key === 'skipped') color = 'text-gray-500';
        if (key === 'zeroValuesNormalized' && data[key] > 0) color = 'text-amber-600';

        tableData.push({ key, field: getFieldLabel(key), value: data[key], color });
      }
    });

    const logs: string[] = data?.normalizationLogs || [];

    return (
      <Card className="mb-5 rounded-xl border-green-200 bg-green-50/30 shadow-sm">
        <div className="mb-4 flex items-start gap-3">
          <CheckCircle className="mt-0.5 shrink-0 text-green-600" size={24} />
          <div>
            <h4 className="text-base font-semibold text-green-800 sm:text-lg">
              {data?.message || t('processCompleted')}
            </h4>
          </div>
        </div>

        <div className="w-full overflow-hidden rounded-lg border border-gray-100 bg-white">
          <Table
            columns={columns}
            dataSource={tableData}
            pagination={false}
            size="small"
            bordered={false}
            className="w-full"
          />
        </div>

        {logs?.length > 0 && (
          <div className="mt-4 rounded-lg border border-amber-100 bg-amber-50/50 p-3">
            <div className="max-h-36 space-y-1 overflow-y-auto pr-1 text-xs text-amber-700">
              {logs.map((log, idx) => (
                <p
                  key={idx}
                  className="break-words border-b border-amber-100/50 pb-1 leading-relaxed last:border-0"
                >
                  • {log}
                </p>
              ))}
            </div>
          </div>
        )}
      </Card>
    );
  }

  return (<></>);
};
